import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../context/UserContext';
import './onboarding.css';

function OnboardingModal() {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);
  const closeBtn = useRef(null);
  const { user, updateUser } = useUser();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const seen = localStorage.getItem('seen_onboarding');
    if (!seen) {
      setVisible(true);
      setTimeout(() => closeBtn.current && closeBtn.current.focus(), 100);
    }
  }, []);

  // Move handleClose and persist functions above the focus-trap so hooks don't capture later-defined variables
  const persistConsentServer = async () => {
    if (!user || !user._id) return;
    try {
      await fetch('/api/ads/consent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': localStorage.getItem('token'),
        },
        body: JSON.stringify({ consent: 'granted' }),
      });
      updateUser({ adConsent: 'granted' });
    } catch (err) {
      // best-effort; ignore
      console.error('Failed to persist onboarding consent to server', err);
    }
  };

  const handleClose = async () => {
    try {
      localStorage.setItem('seen_onboarding', 'true');
      // Persist consent as granted when user accepts onboarding
      localStorage.setItem('ads_consent', 'granted');
      await persistConsentServer();
    } catch (e) {
      // ignore
    }
    setVisible(false);
  };

  // Trap focus inside modal
  useEffect(() => {
    if (!visible || !containerRef.current) return;
    const node = containerRef.current;
    const focusable = node.querySelectorAll('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Mark onboarding as seen but do not grant consent automatically when dismissed via Escape
        try { localStorage.setItem('seen_onboarding', 'true'); } catch (err) {}
        setVisible(false);
        return;
      }
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="onboarding-backdrop" role="dialog" aria-modal="true" aria-label="Welcome to RecipeCore" aria-describedby="onboarding-consent">
      <div className="onboarding-modal" ref={containerRef}>
        <h2>Welcome to RecipeCore</h2>
        <p className="text-muted">Quick tips: Use the extractor to save recipes, view saved recipes in your profile, and remove ads with a one-time payment.</p>
        <p id="onboarding-consent" className="text-muted"><strong>By using this site you agree to seeing ads and consent to basic ad delivery.</strong> You can opt out of tracking-based ads in your profile or pay to remove ads entirely.</p>
        <ul>
          <li>Extract recipes from any supported URL.</li>
          <li>Save recipes to your collection.</li>
          <li>Manage ad preferences and subscriptions in your profile.</li>
        </ul>
        <div className="actions">
          <a className="privacy-link" href="/privacy" rel="noopener">Privacy policy</a>
          <a className="btn btn-link" href="/profile">Remove ads (one-time)</a>
          <button ref={closeBtn} className="btn btn-primary" onClick={handleClose} aria-label="Acknowledge onboarding and accept ads">Got it</button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingModal;
